import {
  useEffect,
  useRef,
  type CSSProperties,
  type KeyboardEvent,
  type ReactNode,
  type RefObject,
} from 'react';

import { cx } from './classNames';
import { Portal } from './Portal';
import { usePopupPosition } from './usePopupPosition';

/**
 * Listen-Popup für `DropDownList` und `ComboBox` — Pendant zu
 * `.e-popup.e-ddl` mit `.e-dropdownbase`/`.e-list-item` von Syncfusion.
 */
export interface ListPopupProps<TItem> {
  open: boolean;
  anchorRef: RefObject<HTMLElement | null>;
  popupRef: RefObject<HTMLDivElement | null>;
  items: TItem[];
  getKey: (item: TItem, index: number) => string;
  getText: (item: TItem) => string;
  isActive?: (item: TItem) => boolean;
  focusedIndex: number;
  onSelect: (item: TItem, index: number) => void;
  onFocusIndex: (index: number) => void;
  itemTemplate?: (item: TItem) => ReactNode;
  allowFiltering?: boolean;
  filterText?: string;
  onFilterChange?: (text: string) => void;
  /** Tastatursteuerung der Liste, solange der Fokus im Suchfeld steht. */
  onFilterKeyDown?: (event: KeyboardEvent<HTMLInputElement>) => void;
  /** Wird beim Scrollen ans Listenende gemeldet (Nachladen). */
  onScrollEnd?: () => void;
  /** Standardtext von Syncfusion. */
  noRecordsText?: string;
}

const LIST_MAX_HEIGHT: CSSProperties = { maxHeight: 300 };

export function ListPopup<TItem>(props: ListPopupProps<TItem>) {
  const { open, anchorRef, popupRef, onScrollEnd } = props;
  const filterRef = useRef<HTMLInputElement>(null);
  const popupStyle = usePopupPosition({ open, anchorRef, popupRef, matchAnchorWidth: true });

  useEffect(() => {
    if (open && props.allowFiltering === true) filterRef.current?.focus();
  }, [open, props.allowFiltering]);

  if (!open) return null;

  return (
    <Portal>
      <div ref={popupRef} className="e-popup e-ddl" style={popupStyle}>
        {props.allowFiltering === true ? (
          <span className="e-input-group e-filter-parent">
            <input
              ref={filterRef}
              type="text"
              className="e-input"
              value={props.filterText ?? ''}
              onChange={(event) => {
                props.onFilterChange?.(event.target.value);
              }}
              onKeyDown={props.onFilterKeyDown}
            />
            <span className="e-input-group-icon e-search-icon e-icons fa-solid fa-magnifying-glass" />
          </span>
        ) : null}
        {props.items.length === 0 ? (
          <div className="e-dropdownbase e-nodata">{props.noRecordsText ?? 'No records found'}</div>
        ) : (
          <div
            className="e-dropdownbase e-content"
            style={LIST_MAX_HEIGHT}
            onScroll={(event) => {
              if (onScrollEnd === undefined) return;
              const element = event.currentTarget;
              if (element.scrollTop + element.clientHeight >= element.scrollHeight - 8) {
                onScrollEnd();
              }
            }}
          >
            <ul className="e-list-parent" role="listbox">
              {props.items.map((item, index) => {
                const active = props.isActive?.(item) === true;
                return (
                  <li
                    key={props.getKey(item, index)}
                    role="option"
                    aria-selected={active}
                    className={cx(
                      'e-list-item',
                      active && 'e-active',
                      index === props.focusedIndex && 'e-item-focus',
                    )}
                    onMouseEnter={() => {
                      props.onFocusIndex(index);
                    }}
                    onClick={() => {
                      props.onSelect(item, index);
                    }}
                  >
                    {props.itemTemplate?.(item) ?? props.getText(item)}
                  </li>
                );
              })}
            </ul>
          </div>
        )}
      </div>
    </Portal>
  );
}

export default ListPopup;
